import {Main} from '../../components/main/main.tsx';
import {Page} from '../../components/page/page.tsx';
import {PageTitle} from '../../components/page/page_title.tsx';
import {__} from '../../support/lang/translations.ts';
import {PageContent} from '../../components/page/page_content.tsx';
import {Card} from '../../components/card/card.tsx';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {FaultsGrid} from './faults_grid.tsx';
import {FaultsGridItem} from './faults_grid_item.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {FlexColumn} from '../../components/utility/flex_column.tsx';
import {AltButton} from '../../components/button/alt_button.tsx';
import {ButtonPosition} from '../../enums/button_position.ts';
import {Button} from '../../components/button/button.tsx';
import {Spacer} from '../../components/utility/spacer.tsx';
import {VehicleSelect} from '../../components/vehicle/vehicle_select.tsx';
import {useAppDispatch, useAppSelector} from '../../store/store.ts';
import {useEffect, useState} from 'react';
import {fetchIndicators} from '../../store/slices/failure_indicators_slice.ts';
import {CreateVehicleModal} from '../../components/vehicle/create_vehicle_modal.tsx';
import {Status} from '../../enums/status.ts';
import {CenteredSpinner} from '../../components/spinner/centered_spinner.tsx';

export function Faults() {
    const dispatch = useAppDispatch();
    const customerInfo = useAppSelector((state) => state.customerInfo);

    const failureIndicators = useAppSelector((state) => state.failureIndicators);
    const vehicles = customerInfo.me?.customer?.vehicles;
    const [selectedVehicleId, setSelectedVehicleId] = useState(
        vehicles && vehicles.length > 0 ? vehicles[0]?.id : null,
    );

    const [brand, setBrand] = useState(
        vehicles?.find((vehicle) => vehicle?.id === selectedVehicleId)?.brand?.toLowerCase() ?? 'default',
    );

    const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);

    function handleVehicleIdChange(vehicleId: number) {
        setSelectedVehicleId(vehicleId);
        setBrand(vehicles?.find((vehicle) => vehicle?.id === vehicleId)?.brand?.toLowerCase() ?? 'default');
    }

    useEffect(() => {
        dispatch(
            fetchIndicators({
                brand: brand,
            }),
        );
    }, [dispatch, brand]);

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    //state variables remain null so require manual updating
    if (selectedVehicleId === null && vehicles && vehicles.length > 0) {
        const vehicle = vehicles[0];
        setSelectedVehicleId(vehicle?.id);
        setBrand(vehicle?.brand ?? 'default');
    }

    return (
        <Main>
            {isCreateVehicleModalOpen && (
                <CreateVehicleModal onCloseModal={() => setIsCreateVehicleModalOpen(false)} vehicles={vehicles} />
            )}
            <Page>
                <PageTitle>
                    <h1>{__('faults.titles.index')}</h1>
                </PageTitle>

                <PageContent>
                    <p className="text-center">{__('faults.copy.index')}</p>
                </PageContent>
                <PageContent>
                    {selectedVehicleId && vehicles ? (
                        <>
                            <VehicleSelect
                                onChange={handleVehicleIdChange}
                                vehicles={vehicles}
                                selectedVehicleId={selectedVehicleId}
                            />

                            <Card>
                                <FaultsGrid>
                                    {failureIndicators.failureIndicators?.map((indicator, index) => (
                                        <FaultsGridItem indicator={indicator} key={index} />
                                    ))}
                                </FaultsGrid>

                                {failureIndicators.failureIndicators?.length === 0 && (
                                    <div style={{padding: '2rem'}}>
                                        <h3 className="text-center">{__('faults.empty.title')}</h3>
                                    </div>
                                )}
                            </Card>
                        </>
                    ) : (
                        <>
                            <Card>
                                <FlexRow>
                                    <FlexColumn alignment={Alignment.Start}>
                                        <strong>{__('faults.buttons.add_vehicle')}</strong>
                                    </FlexColumn>
                                    <FlexColumn alignment={Alignment.End}>
                                        <FlexColumn alignment={Alignment.End}>
                                            <AltButton
                                                position={ButtonPosition.Right}
                                                onClick={() => setIsCreateVehicleModalOpen(true)}
                                            >
                                                <i className="fa-light fa-plus" />
                                            </AltButton>
                                        </FlexColumn>
                                    </FlexColumn>
                                </FlexRow>
                            </Card>

                            <Card>
                                <FaultsGrid disabled={true}>
                                    {failureIndicators.failureIndicators?.map((indicator, index) => (
                                        <FaultsGridItem indicator={indicator} key={index} />
                                    ))}
                                </FaultsGrid>
                            </Card>
                        </>
                    )}
                </PageContent>
                <PageContent>
                    <FlexRow alignment={Alignment.Center}>
                        <p>{__('faults.titles.support')}</p>
                    </FlexRow>
                    <Spacer />
                    <FlexRow alignment={Alignment.Center}>
                        <Button to={'/chat'}>
                            <i className={'fa-regular fa-comment'} />
                            <span>{__('faults.buttons.support')}</span>
                        </Button>
                    </FlexRow>
                </PageContent>
            </Page>
        </Main>
    );
}
